import {JSX} from 'react';
import {styled, useTheme} from 'styled-components';

import {Custom} from '@shared_web/lib/react';
import {cssPx, optional, optionalRaw} from '@shared_web/lib/styled_utils';

const DEFAULT_ICON_SIZE = 24;

export interface SvgIconData {
  viewBox: string;
  element: JSX.Element;
}

interface SvgIconProps {
  icon: SvgIconData;
  width?: number | string;
  height?: number | string;
  size?: number | string;
  color?: string;
  colorHover?: string;
  rotate?: number;
}

export const SvgIcon: Custom<SvgIconProps, 'svg'> = props => {
  const theme = useTheme();
  const {
    icon,
    size,
    width,
    height,
    color = theme.main?.textColor ?? 'black',
    colorHover,
    onClick,
    rotate,
    ...rest
  } = props;
  const {viewBox, element} = icon;
  const w = width ?? size;
  const h = height ?? size;
  const heightOverride = w === undefined && h === undefined ? DEFAULT_ICON_SIZE : undefined;
  const handleClick = onClick;
  return (
    <ColoredSvg
      viewBox={viewBox}
      $width={w}
      $height={heightOverride ?? h}
      $fill={color}
      $fillHover={colorHover ?? color}
      $clickable={onClick !== undefined}
      $rotate={rotate}
      onClick={handleClick}
      {...rest}
    >
      {element}
    </ColoredSvg>
  );
};
SvgIcon.displayName = 'SvgIcon';

const ColoredSvg = styled.svg<{
  $fill?: string;
  $fillHover?: string;
  $clickable?: boolean;
  $width: number | string | undefined;
  $height: number | string | undefined;
  $rotate?: number;
}>`
  ${p => optional('fill', p.$fill)}
  ${p => optionalRaw(p.$fillHover, v => `&:hover { fill: ${v}; }`)}
  ${p => optionalRaw(p.$width, v => `width: ${cssPx(v)};`)}
  ${p => optionalRaw(p.$height, v => `height: ${cssPx(v)};`)}
  ${p => (p.$clickable ? 'cursor: pointer;' : undefined)}
  ${p => optionalRaw(p.$rotate, v => `transform: rotate(${v}deg);`)}
`;
